/**
 * `/settings/webhooks/:uuid/deliveries` — per-endpoint deliveries log.
 *
 * Full-page surface (not a slide-over) — users come here to triage
 * failed deliveries, replay individual messages, and inspect attempt
 * history. The list routes user back via a "Back to webhooks"
 * action.
 *
 * Data plane:
 *   - `useMessages({ endpoint_uuid, event_type?, status? })` — calls
 *     `GET /v1/webhooks/messages`. Cursor-paginated; we hold the
 *     cursor in local state and paginate via a "Load more" button at
 *     the bottom. No infinite-scroll (the log is rarely big enough
 *     to need it; a 50-row default is plenty for triage).
 *   - `useAttempts(messageUuid)` — enabled only when the user
 *     expands a row. React-Query dedups if they expand+collapse+re-
 *     expand the same row.
 *   - `useReplayMessage()` — POST; on success we surface
 *     "Replay enqueued" (when `enqueued: true`) vs. "Replay
 *     scheduled" (cron fallback, `enqueued: false`).
 */
import { EmptyState } from '@/components/primitives/EmptyState';
import { ErrorState } from '@/components/primitives/ErrorState';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toastFromError, toastInfo, toastSuccess } from '@/lib/errors';
import { relativeTime } from '@/lib/format';
import type { WebhookMessage, WebhookMessageStatus } from '@/lib/queries';
import { useAttempts, useEndpoint, useMessages, useReplayMessage } from '@/lib/queries';
import { ArrowClockwise, ListBullets, Play } from '@phosphor-icons/react';
import { createFileRoute } from '@tanstack/react-router';
import { type ReactElement, useState } from 'react';

export const Route = createFileRoute('/_app/settings/webhooks/$uuid/deliveries')({
  component: DeliveriesPage,
});

const STATUS_OPTIONS: { value: 'any' | WebhookMessageStatus; label: string }[] = [
  { value: 'any', label: 'Any status' },
  { value: 'pending', label: 'Pending' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'failed', label: 'Failed' },
  { value: 'dead', label: 'Dead' },
];

const EVENT_OPTIONS: { value: string; label: string }[] = [
  { value: 'any', label: 'Any event' },
  { value: 'audit.completed', label: 'audit.completed' },
  { value: 'audit.failed', label: 'audit.failed' },
  { value: 'audit.started', label: 'audit.started' },
  { value: 'monitor.run.failed', label: 'monitor.run.failed' },
  { value: 'usage.threshold.warning', label: 'usage.threshold.warning' },
  { value: 'usage.threshold.critical', label: 'usage.threshold.critical' },
];

function DeliveriesPage(): ReactElement {
  const { uuid } = Route.useParams();
  const endpoint = useEndpoint(uuid);

  const [status, setStatus] = useState<'any' | WebhookMessageStatus>('any');
  const [eventType, setEventType] = useState<string>('any');

  const messages = useMessages({
    endpoint_uuid: uuid,
    status: status === 'any' ? undefined : status,
    event_type: eventType === 'any' ? undefined : eventType,
    limit: 50,
  });

  const rows = messages.data?.messages ?? [];

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          {/* No "Back to webhooks" button: the settings breadcrumb already
              carries a Webhooks crumb directly above this header. */}
          <div>
            <h2 className="text-base font-semibold text-[var(--color-ink)]">Deliveries</h2>
            <p className="text-sm text-[var(--color-mute)]">
              Every event dispatched to{' '}
              <span className="font-mono text-xs">{endpoint.data?.url ?? '…'}</span>. Click a row to
              see individual attempt timings + response bodies. Use "Replay" to re-send any message.
            </p>
          </div>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={status}
          onValueChange={(v) => {
            if (typeof v === 'string') setStatus(v as 'any' | WebhookMessageStatus);
          }}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={eventType}
          onValueChange={(v) => {
            if (typeof v === 'string') setEventType(v);
          }}
        >
          <SelectTrigger className="w-52">
            <SelectValue placeholder="Event type" />
          </SelectTrigger>
          <SelectContent>
            {EVENT_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {messages.isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      )}

      {messages.isError && !messages.isLoading && (
        <ErrorState
          title="We couldn't load deliveries"
          error={messages.error}
          retry={() => {
            void messages.refetch();
          }}
        />
      )}

      {!messages.isLoading && !messages.isError && rows.length === 0 && (
        <EmptyState
          icon={ListBullets}
          title="No deliveries yet"
          description="Once events fire for this endpoint, each attempt will appear here with its status and response body excerpt."
        />
      )}

      {!messages.isLoading && !messages.isError && rows.length > 0 && (
        <section className="overflow-hidden rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)]">
          <Accordion>
            {rows.map((row) => (
              <MessageRow key={row.uuid} message={row} />
            ))}
          </Accordion>
        </section>
      )}
    </div>
  );
}

function MessageRow({ message }: { message: WebhookMessage }): ReactElement {
  const replay = useReplayMessage();

  async function onReplay(event: React.MouseEvent<HTMLButtonElement>): Promise<void> {
    // Stop the accordion trigger from toggling when the user clicks
    // the inline "Replay" button.
    event.stopPropagation();
    try {
      const res = await replay.mutateAsync({ uuid: message.uuid });
      if (res.enqueued) {
        toastSuccess('Replay enqueued', `New message ${res.message_uuid} is on the queue.`);
      } else {
        toastInfo(
          'Replay scheduled',
          `New message ${res.message_uuid} will be sent by the retry sweep.`,
        );
      }
    } catch (err) {
      toastFromError(err);
    }
  }

  return (
    <AccordionItem value={message.uuid}>
      <AccordionTrigger className="px-4 hover:bg-[var(--color-paper-alt)]/40">
        <div className="flex flex-1 items-center gap-6 text-sm">
          <span className="font-mono text-xs text-[var(--color-ink-soft)]">{message.uuid}</span>
          <span className="font-mono text-xs text-[var(--color-ink)]">{message.event_type}</span>
          <MessageStatusBadge status={message.status} />
          <span className="text-xs text-[var(--color-mute)]">
            attempt {message.attempt_count} · {message.last_response_status ?? '—'}
          </span>
          <span className="ml-auto text-xs text-[var(--color-mute)]">
            {relativeTime(message.succeeded_at ?? message.failed_at ?? message.created_at)}
          </span>
          <Button
            size="sm"
            variant="ghost"
            aria-label={`Replay ${message.uuid}`}
            onClick={onReplay}
            loading={replay.isPending}
          >
            <ArrowClockwise aria-hidden="true" />
            Replay
          </Button>
        </div>
      </AccordionTrigger>
      <AccordionContent className="bg-[var(--color-paper-alt)] px-4">
        <AttemptsPanel messageUuid={message.uuid} />
      </AccordionContent>
    </AccordionItem>
  );
}

function AttemptsPanel({ messageUuid }: { messageUuid: string }): ReactElement {
  const attempts = useAttempts(messageUuid);

  if (attempts.isLoading) {
    return (
      <div className="space-y-2 py-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (attempts.isError) {
    return (
      <ErrorState
        title="We couldn't load attempts"
        error={attempts.error}
        retry={() => {
          void attempts.refetch();
        }}
      />
    );
  }

  const rows = attempts.data?.attempts ?? [];
  if (rows.length === 0) {
    return (
      <p className="py-3 text-xs text-[var(--color-mute)]">
        No attempts recorded yet — the first attempt will appear here within a few seconds of the
        message being enqueued.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="border-b border-[var(--color-line-soft)] text-left uppercase tracking-[0.06em] text-[var(--color-mute)]">
            <th className="py-2 pr-4 font-medium">#</th>
            <th className="py-2 pr-4 font-medium">Reason</th>
            <th className="py-2 pr-4 font-medium">Status</th>
            <th className="py-2 pr-4 font-medium">Duration</th>
            <th className="py-2 pr-4 font-medium">Requested</th>
            <th className="py-2 pr-4 font-medium">Response</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.uuid} className="border-b border-[var(--color-line-soft)]/70">
              <td className="py-2 pr-4 font-mono text-[var(--color-ink-soft)]">
                {a.attempt_number}
              </td>
              <td className="py-2 pr-4 font-mono">{a.reason}</td>
              <td className="py-2 pr-4 font-mono">{a.status_code ?? a.error ?? '—'}</td>
              <td className="py-2 pr-4">{a.duration_ms === null ? '—' : `${a.duration_ms}ms`}</td>
              <td className="py-2 pr-4 text-[var(--color-mute)]">{relativeTime(a.requested_at)}</td>
              <td className="max-w-md truncate py-2 pr-4 font-mono text-[var(--color-ink-soft)]">
                {a.response_body_excerpt ?? a.error ?? '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MessageStatusBadge({ status }: { status: WebhookMessageStatus }): ReactElement {
  const toneMap: Record<WebhookMessageStatus, { bg: string; fg: string }> = {
    pending: {
      bg: 'bg-[var(--color-paper-alt)]',
      fg: 'text-[var(--color-ink-soft)]',
    },
    succeeded: {
      bg: 'bg-[var(--color-success)]/10',
      fg: 'text-[var(--color-success)]',
    },
    failed: {
      bg: 'bg-[var(--color-danger)]/10',
      fg: 'text-[var(--color-danger)]',
    },
    dead: {
      bg: 'bg-[var(--color-danger)]/10',
      fg: 'text-[var(--color-danger)]',
    },
  };
  const tone = toneMap[status];
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${tone.bg} ${tone.fg}`}
    >
      {status}
    </span>
  );
}
